import React from 'react';
import { ArrowLeft, Award, BookOpen, Mic, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage, getPageTexts } from '../../i18n/pageTranslations';

const SubpageCard = ({ title, icon: Icon, imageUrl, link }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      onClick={() => navigate(link)}
      className="relative h-64 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-500 border-none rounded-none"
    >
      <div className="absolute inset-0">
        <img 
          src={imageUrl} 
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3d5a6f]/70 via-[#3d5a6f]/60 to-[#1C1C1A]/80"></div>
      </div>
      <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
        <Icon className="text-white mb-4" size={40} />
        <h4 className="text-lg md:text-xl font-light text-white uppercase tracking-wide leading-tight">
          {title}
        </h4>
      </div>
    </Card>
  );
};

const Doctor = () => {
  const navigate = useNavigate();
  const lang = useLanguage();
  const t = getPageTexts('rinoDoctor', lang);

  const subpages = [
    {
      title: t.subpages?.curriculum || 'Currículum',
      icon: Award,
      image: 'https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/rinomodelacion/doctor/curriculum'
    },
    {
      title: t.subpages?.press || 'Artículos de Prensa',
      icon: BookOpen,
      image: 'https://images.pexels.com/photos/6393342/pexels-photo-6393342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/rinomodelacion/doctor/prensa'
    },
    {
      title: t.subpages?.presentations || 'Ponencias Congresos',
      icon: Mic,
      image: 'https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/rinomodelacion/doctor/ponencias'
    },
    {
      title: t.subpages?.clinic || 'Clínica Marbella',
      icon: MapPin,
      image: 'https://images.pexels.com/photos/3825527/pexels-photo-3825527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/rinomodelacion/contacto/marbella'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.back}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          
          {/* Quote */}
          <div className="bg-gradient-to-r from-cream to-blue-50 p-8 rounded-none mb-12">
            <p className="text-xl text-text-secondary italic mb-6 leading-relaxed text-center">
              {t.quote}
            </p>
            <p className="text-center text-[#1C1C1A] font-semibold text-xl">
              — Dr. Ángel Ruiz Norro
            </p>
          </div>

          {/* Bio */}
          <div className="prose max-w-none mb-12">
            <p className="text-lg text-text-secondary leading-relaxed mb-6">
              {t.description}
            </p>
          </div>

          {/* Subpages Cards */}          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {subpages.map((page, index) => (
              <SubpageCard 
                key={index}
                title={page.title}
                icon={page.icon}
                imageUrl={page.image}
                link={page.link}
              />
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="text-2xl font-light text-[#1C1C1A] mb-6">{t.ctaTitle}</h3>
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded-none"
            >
              {t.ctaButton}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Doctor;
